import { useEffect, useMemo, useState } from 'react'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import moment from 'moment'
import { useExternalApiAuditLogStore } from 'Store/ExternalApiAuditLog'

const PAGE_SIZE = 10

export type SortDirection = 'asc' | 'desc'
export type SortableField =
  | 'createdAt'
  | 'endpoint'
  | 'statusCode'
  | 'status'
  | 'errorMessage'

const ViewModel = () => {
  const { data, isLoading, getAll, clearState } = useExternalApiAuditLogStore(
    state => ({
      data: state.data,
      isLoading: state.loading,
      getAll: state.getAll,
      clearState: state.clearState,
    }),
    shallow
  )

  const [page, setPage] = useState(1)
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const [isError, setIsError] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortField, setSortField] = useState<SortableField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const formatDate = (value: string | null) => {
    return value ? moment(value).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss') : '-'
  }

  const statusOptions = useMemo(() => {
    return Array.from(new Set(data.map(log => log.status).filter(Boolean)))
  }, [data])

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase()
    return data.filter(log => {
      if (statusFilter && log.status !== statusFilter) return false

      if (dateFrom || dateTo) {
        const createdDay = moment(log.createdAt).tz('Asia/Bangkok').format('YYYY-MM-DD')
        if (dateFrom && createdDay < dateFrom) return false
        if (dateTo && createdDay > dateTo) return false
      }

      if (term) {
        const haystack = [
          log.endpoint,
          log.httpMethod,
          log.status,
          log.statusCode,
          log.clientId,
          log.ipAddress,
          log.userAgent,
          log.errorMessage,
          log.requestBody,
          log.responseBody,
          formatDate(log.createdAt),
        ]
          .filter(v => v != null)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(term)) return false
      }

      return true
    })
  }, [data, search, statusFilter, dateFrom, dateTo])

  const sortedData = useMemo(() => {
    const copy = [...filteredData]
    copy.sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      let comparison = 0
      if (sortField === 'createdAt') {
        comparison = new Date(aValue).getTime() - new Date(bValue).getTime()
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else {
        comparison = aValue.toString().localeCompare(bValue.toString())
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
    return copy
  }, [filteredData, sortField, sortDirection])

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedData.length / PAGE_SIZE))
  }, [sortedData.length])

  const pagedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return sortedData.slice(start, start + PAGE_SIZE)
  }, [sortedData, page])

  const fetchData = () => {
    setIsError(false)
    getAll().then(({ success, data: resData }) => {
      if (!success) {
        setIsError(true)
        toast.error('เกิดข้อผิดพลาดในการดึงข้อมูล' + (resData ? ' : ' + resData : ''))
      }
    })
  }

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const isExpanded = (id: number) => expandedIds.includes(id)

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
    setExpandedIds([])
  }

  const onSort = (field: SortableField) => {
    setExpandedIds([])
    setPage(1)
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const onSearchChange = (value: string) => {
    setSearch(value)
    setPage(1)
    setExpandedIds([])
  }

  const onStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    setPage(1)
    setExpandedIds([])
  }

  const onDateRangeChange = (from: string, to: string) => {
    setDateFrom(from)
    setDateTo(to)
    setPage(1)
    setExpandedIds([])
  }

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
    setExpandedIds([])
  }

  const prettyJson = (value: string | null) => {
    if (!value) return '-'
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  }

  useEffect(() => {
    fetchData()
    return () => {
      clearState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    data,
    pagedData,
    isLoading,
    isError,
    page,
    totalPages,
    pageSize: PAGE_SIZE,
    filteredCount: filteredData.length,
    search,
    onSearchChange,
    statusFilter,
    statusOptions,
    onStatusFilterChange,
    dateFrom,
    dateTo,
    onDateRangeChange,
    clearFilters,
    isExpanded,
    sortField,
    sortDirection,
    onSort,
    toggleExpand,
    goToPage,
    formatDate,
    prettyJson,
  }
}

export default ViewModel
